import type { ReactNode } from 'react'
import { cn } from '../cn'
import { CollectionPagination } from '../collections/CollectionPagination'
import { CollectionToolbar } from '../collections/CollectionToolbar'
import { DataTableV2 } from '../collections/DataTableV2'
import type { DataTableV2Props } from '../collections/DataTableV2'
import type { UseCollectionResult } from '../collections/useCollection'
import { proTableDataAttrs } from './markers'

export type ProTableProps<RowT> = {
  collection: UseCollectionResult<RowT>
  /** Optional toolbar primary action slot */
  primaryAction?: ReactNode
  searchable?: boolean
  searchPlaceholder?: string
  searchAriaLabel?: string
  stableChipNames?: boolean
  className?: string
  tableProps?: Omit<DataTableV2Props<RowT>, 'collection'>
  showPagination?: boolean
}

/**
 * ProTable-1 — facade over useCollection chrome:
 * CollectionToolbar + DataTableV2 + CollectionPagination.
 * Apps pass a bound collection + column defs only.
 */
export function ProTable<RowT>({
  collection,
  primaryAction,
  searchable = true,
  searchPlaceholder,
  searchAriaLabel,
  stableChipNames = false,
  className = '',
  tableProps,
  showPagination = true,
}: ProTableProps<RowT>) {
  return (
    <div {...proTableDataAttrs()} className={cn('flex min-w-0 flex-col gap-3', className)}>
      <CollectionToolbar
        collection={collection}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        searchAriaLabel={searchAriaLabel}
        primaryAction={primaryAction}
        stableChipNames={stableChipNames}
      />
      <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain shadow-scroll-hint">
        <DataTableV2 collection={collection} {...tableProps} />
      </div>
      {showPagination ? (
        <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain shadow-scroll-hint">
          <CollectionPagination collection={collection} />
        </div>
      ) : null}
    </div>
  )
}
